import { useState } from 'react'
import { Account, CreateAccountRequest, UpdateAccountRequest, SummaryData, Entry, AccountWithLatestEntry } from '../../../../../shared/types'
import { useApiQuery, useApiMutation, ApiException, apiRequest } from '../../../shared/hooks/useApi'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { LoadingSpinner, ErrorBoundary, ErrorDisplay, ConfirmationDialog } from '../../../shared/components'
import AccountForm from './AccountForm'
import AccountRow from './AccountRow'
import EmptyAccountsState from './EmptyAccountsState'

interface AccountWithValues extends Account {
  liquidValue: number
  semiLiquidValue: number
  illiquidValue: number
}

export default function AccountsList() {
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [editingAccount, setEditingAccount] = useState<Account | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean
    accountId: number | null
    accountName: string
  }>({ isOpen: false, accountId: null, accountName: '' })

  // Fetch accounts
  const {
    data: accounts = [],
    isLoading: isLoadingAccounts,
    error: accountsError,
    refetch: refetchAccounts
  } = useApiQuery<Account[]>(['accounts'], '/accounts')

  // Fetch dashboard summary to get account values
  const {
    data: summaryData,
    isLoading: isLoadingSummary,
    error: summaryError
  } = useApiQuery<SummaryData>(['dashboard', 'summary'], '/dashboard/summary')

  // Create account mutation
  const createAccountMutation = useApiMutation<Account, CreateAccountRequest>(
    '/accounts',
    'POST',
    {
      onSuccess: () => {
        setShowForm(false)
        setError(null)
        refetchAccounts()
      },
      onError: (error: ApiException) => {
        setError(error.message)
      },
      invalidateQueries: [['accounts'], ['dashboard', 'summary']]
    }
  )

  // Update account mutation
  const updateAccountMutation = useApiMutation<Account, { id: number; data: UpdateAccountRequest }>(
    ({ id }) => `/accounts/${id}`,
    'PUT',
    {
      onSuccess: () => {
        setEditingAccount(null)
        setShowForm(false)
        setError(null)
        refetchAccounts()
      },
      onError: (error: ApiException) => {
        setError(error.message)
      },
      invalidateQueries: [['accounts'], ['dashboard', 'summary']]
    }
  )

  // Delete account mutation
  const deleteAccountMutation = useApiMutation<{ success: boolean }, number>(
    (id) => `/accounts/${id}`,
    'DELETE',
    {
      onSuccess: () => {
        setError(null)
        refetchAccounts()
      },
      onError: (error: ApiException) => {
        setError(error.message)
      },
      invalidateQueries: [['accounts'], ['dashboard', 'summary']]
    }
  )

  // Update entry values mutation
  const updateEntryMutation = useMutation({
    mutationFn: ({ accountId, values }: {
      accountId: number
      values: { liquidValue: number; semiLiquidValue: number; illiquidValue: number, entryDate: string }
    }) => {
      // Send only the values as the request body
      return apiRequest<Entry>(`/accounts/${accountId}/entries`, {
        method: 'POST',
        body: JSON.stringify(values)
      })
    },
    onSuccess: () => {
      setError(null)
      // Invalidate queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'summary'] })
    },
    onError: (error: ApiException) => {
      setError(error.message)
    }
  })

  const handleCreateAccount = (data: CreateAccountRequest) => {
    createAccountMutation.mutate(data)
  }

  const handleUpdateAccount = (data: UpdateAccountRequest) => {
    if (!editingAccount) return
    updateAccountMutation.mutate({ id: editingAccount.id, data })
  }

  const handleDeleteAccount = (accountId: number) => {
    const account = accounts.find((acc: Account) => acc.id === accountId)
    if (account) {
      setDeleteConfirmation({
        isOpen: true,
        accountId,
        accountName: account.name
      })
    }
  }

  const confirmDeleteAccount = () => {
    if (deleteConfirmation.accountId) {
      deleteAccountMutation.mutate(deleteConfirmation.accountId)
      setDeleteConfirmation({ isOpen: false, accountId: null, accountName: '' })
    }
  }

  const cancelDeleteAccount = () => {
    setDeleteConfirmation({ isOpen: false, accountId: null, accountName: '' })
  }

  const handleUpdateValues = (accountId: number, values: {
    liquidValue: number
    semiLiquidValue: number
    illiquidValue: number
  }) => {
    const today = new Date().toISOString().split('T')[0]
    updateEntryMutation.mutate({
      accountId,
      values: {
        ...values,
        entryDate: today
      }
    })
  }

  const handleEditAccount = (account: Account) => {
    setEditingAccount(account)
    setShowForm(true)
  }

  const handleCloseForm = () => {
    setShowForm(false)
    setEditingAccount(null)
    setError(null)
  }

  // Combine accounts with their latest values
  const accountsWithValues: AccountWithValues[] = accounts.map((account: Account) => {
    const accountSummary = summaryData?.accounts.find((a: AccountWithLatestEntry) => a.id === account.id)
    const latestEntry = accountSummary?.latestEntry

    return {
      ...account,
      liquidValue: latestEntry?.liquidValue || 0,
      semiLiquidValue: latestEntry?.semiLiquidValue || 0,
      illiquidValue: latestEntry?.illiquidValue || 0
    }
  })

  // Group accounts by type
  const assets = accountsWithValues.filter(account => account.type === 'asset')
  const liabilities = accountsWithValues.filter(account => account.type === 'liability')

  // Calculate totals
  const assetTotals = assets.reduce((totals, account) => ({
    liquid: totals.liquid + account.liquidValue,
    semiLiquid: totals.semiLiquid + account.semiLiquidValue,
    illiquid: totals.illiquid + account.illiquidValue,
    total: totals.total + account.liquidValue + account.semiLiquidValue + account.illiquidValue
  }), { liquid: 0, semiLiquid: 0, illiquid: 0, total: 0 })

  const liabilityTotals = liabilities.reduce((totals, account) => ({
    liquid: totals.liquid + account.liquidValue,
    semiLiquid: totals.semiLiquid + account.semiLiquidValue,
    illiquid: totals.illiquid + account.illiquidValue,
    total: totals.total + account.liquidValue + account.semiLiquidValue + account.illiquidValue
  }), { liquid: 0, semiLiquid: 0, illiquid: 0, total: 0 })

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value)
  }

  if (isLoadingAccounts || isLoadingSummary) {
    return <LoadingSpinner />
  }

  if (accountsError || summaryError) {
    return (
      <ErrorDisplay
        error={accountsError || summaryError}
        title="Failed to Load Accounts"
        showRetry={true}
        onRetry={() => {
          refetchAccounts()
        }}
      />
    )
  }

  return (
    <ErrorBoundary>
      <div className="accounts-list">


        <ErrorDisplay error={error} title="Operation Failed" />

        {/* Empty state */}
        {accounts.length === 0 ? (
          <EmptyAccountsState onAddAccount={() => setShowForm(true)} />
        ) : (
          <div style={{
            backgroundColor: 'var(--container-bg)',
            borderRadius: '8px',
            border: '1px solid var(--border-color)',
            overflow: 'hidden'
          }}>
            {/* Table Header with Add Button */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1rem',
              backgroundColor: 'var(--page-bg)',
              borderBottom: '1px solid var(--border-color)'
            }}>
              <h3 style={{
                margin: 0,
                color: 'var(--text-primary)',
                fontSize: '1.1rem',
                fontWeight: '600'
              }}>
                Accounts
              </h3>
              <button
                onClick={() => setShowForm(true)}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: 'var(--primary-blue)',
                  border: 'none',
                  borderRadius: '6px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#0099e6'
                  e.currentTarget.style.transform = 'translateY(-1px)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--primary-blue)'
                  e.currentTarget.style.transform = 'translateY(0)'
                }}
              >
                <span style={{ fontSize: '1rem' }}>+</span>
                Add Account
              </button>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--page-bg)' }}>
                  <th style={{
                    padding: '1rem 0.75rem',
                    textAlign: 'left',
                    color: 'var(--text-light)',
                    fontWeight: '600',
                    borderBottom: '2px solid var(--border-color)'
                  }}>
                    Account Name
                  </th>
                  <th style={{
                    padding: '1rem 0.75rem',
                    textAlign: 'right',
                    color: 'var(--text-light)',
                    fontWeight: '600',
                    borderBottom: '2px solid var(--border-color)'
                  }}>
                    Liquid
                  </th>
                  <th style={{
                    padding: '1rem 0.75rem',
                    textAlign: 'right',
                    color: 'var(--text-light)',
                    fontWeight: '600',
                    borderBottom: '2px solid var(--border-color)'
                  }}>
                    Semi-Liquid
                  </th>
                  <th style={{
                    padding: '1rem 0.75rem',
                    textAlign: 'right',
                    color: 'var(--text-light)',
                    fontWeight: '600',
                    borderBottom: '2px solid var(--border-color)'
                  }}>
                    Illiquid
                  </th>
                  <th style={{
                    padding: '1rem 0.75rem',
                    textAlign: 'right',
                    color: 'var(--text-light)',
                    fontWeight: '600',
                    borderBottom: '2px solid var(--border-color)'
                  }}>
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {/* Assets Section */}
                {assets.length > 0 && (
                  <>
                    {assets.map(account => (
                      <AccountRow
                        key={account.id}
                        account={account}
                        liquidValue={account.liquidValue}
                        semiLiquidValue={account.semiLiquidValue}
                        illiquidValue={account.illiquidValue}
                        onUpdateValues={handleUpdateValues}
                        onEditAccount={handleEditAccount}
                        onDeleteAccount={handleDeleteAccount}
                        isUpdating={updateEntryMutation.isPending}
                      />
                    ))}
                    <tr style={{ backgroundColor: 'var(--page-bg)' }}>
                      <td style={{
                        padding: '0.75rem',
                        fontWeight: '700',
                        color: 'var(--text-light)',
                        borderTop: '2px solid var(--border-color)'
                      }}>
                        Assets Total
                      </td>
                      <td style={{
                        padding: '0.75rem',
                        textAlign: 'right',
                        fontFamily: 'var(--font-mono)',
                        fontWeight: '700',
                        color: 'var(--accent-green)',
                        borderTop: '2px solid var(--border-color)'
                      }}>
                        {formatCurrency(assetTotals.liquid)}
                      </td>
                      <td style={{
                        padding: '0.75rem',
                        textAlign: 'right',
                        fontFamily: 'var(--font-mono)',
                        fontWeight: '700',
                        color: 'var(--accent-green)',
                        borderTop: '2px solid var(--border-color)'
                      }}>
                        {formatCurrency(assetTotals.semiLiquid)}
                      </td>
                      <td style={{
                        padding: '0.75rem',
                        textAlign: 'right',
                        fontFamily: 'var(--font-mono)',
                        fontWeight: '700',
                        color: 'var(--accent-green)',
                        borderTop: '2px solid var(--border-color)'
                      }}>
                        {formatCurrency(assetTotals.illiquid)}
                      </td>
                      <td style={{
                        padding: '0.75rem',
                        textAlign: 'right',
                        fontFamily: 'var(--font-mono)',
                        fontWeight: '700',
                        color: 'var(--accent-green)',
                        borderTop: '2px solid var(--border-color)'
                      }}>
                        {formatCurrency(assetTotals.total)}
                      </td>
                    </tr>
                  </>
                )}

                {/* Liabilities Section */}
                {liabilities.length > 0 && (
                  <>
                    {liabilities.map(account => (
                      <AccountRow
                        key={account.id}
                        account={account}
                        liquidValue={account.liquidValue}
                        semiLiquidValue={account.semiLiquidValue}
                        illiquidValue={account.illiquidValue}
                        onUpdateValues={handleUpdateValues}
                        onEditAccount={handleEditAccount}
                        onDeleteAccount={handleDeleteAccount}
                        isUpdating={updateEntryMutation.isPending}
                      />
                    ))}
                    <tr style={{ backgroundColor: 'var(--page-bg)' }}>
                      <td style={{
                        padding: '0.75rem',
                        fontWeight: '700',
                        color: 'var(--text-light)',
                        borderTop: '2px solid var(--border-color)'
                      }}>
                        Liabilities Total
                      </td>
                      <td style={{
                        padding: '0.75rem',
                        textAlign: 'right',
                        fontFamily: 'var(--font-mono)',
                        fontWeight: '700',
                        color: 'var(--accent-red)',
                        borderTop: '2px solid var(--border-color)'
                      }}>
                        {formatCurrency(liabilityTotals.liquid)}
                      </td>
                      <td style={{
                        padding: '0.75rem',
                        textAlign: 'right',
                        fontFamily: 'var(--font-mono)',
                        fontWeight: '700',
                        color: 'var(--accent-red)',
                        borderTop: '2px solid var(--border-color)'
                      }}>
                        {formatCurrency(liabilityTotals.semiLiquid)}
                      </td>
                      <td style={{
                        padding: '0.75rem',
                        textAlign: 'right',
                        fontFamily: 'var(--font-mono)',
                        fontWeight: '700',
                        color: 'var(--accent-red)',
                        borderTop: '2px solid var(--border-color)'
                      }}>
                        {formatCurrency(liabilityTotals.illiquid)}
                      </td>
                      <td style={{
                        padding: '0.75rem',
                        textAlign: 'right',
                        fontFamily: 'var(--font-mono)',
                        fontWeight: '700',
                        color: 'var(--accent-red)',
                        borderTop: '2px solid var(--border-color)'
                      }}>
                        {formatCurrency(liabilityTotals.total)}
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Account Form Modal */}
        {showForm && (
          <AccountForm
            account={editingAccount || undefined}
            onSubmit={(data) => {
              if (editingAccount) {
                handleUpdateAccount(data as UpdateAccountRequest)
              } else {
                handleCreateAccount(data as CreateAccountRequest)
              }
            }}
            onCancel={handleCloseForm}
            isLoading={createAccountMutation.isPending || updateAccountMutation.isPending}
            error={error}
          />
        )}

        {/* Delete Confirmation Dialog */}
        <ConfirmationDialog
          isOpen={deleteConfirmation.isOpen}
          title="Delete Account"
          message={`Are you sure you want to delete "${deleteConfirmation.accountName}"? This will permanently remove the account and all associated entries. This action cannot be undone.`}
          confirmText="Delete Account"
          cancelText="Cancel"
          onConfirm={confirmDeleteAccount}
          onCancel={cancelDeleteAccount}
          isDestructive={true}
          isLoading={deleteAccountMutation.isPending}
        />
      </div>
    </ErrorBoundary>
  )
}